/* eslint-disable @stylistic/max-len */
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { SwiperOptions } from 'swiper/types';
import { Subscription } from 'rxjs';

import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input,
  NgZone, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, ViewEncapsulation
} from '@angular/core';

import { IStoreFilter } from '../store-filter/store-filter.interface';
import { SdStoreFilterGroupComponent } from '../store-filter-group/store-filter-group.component';

import {
  DeviceService, ICardStore, IGoogleMap, ILocator, IPosition, IProductCard, SdButtonComponent, SdFormInputComponent,
  SdLibConfig, SdLoaderComponent, SdTabComponent, SdTabGroupComponent, SdThemeComponent, SdTitleComponent,
  SizesEnum, StoreTypeEnum, ThemesEnum
} from '@seb/web-design-system/core';

import { IMapLegend, SdGoogleMapComponent, SdMapLegendComponent } from '@seb/web-design-system/maps';
import { SdProductCardComponent } from '@seb/web-design-system/product-card';
import { SdCardStoreComponent } from '@seb/web-design-system/cards';

@Component({
  selector: 'sd-store-locator',
  templateUrl: './store-locator.component.html',
  styleUrls: ['./store-locator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdGoogleMapComponent,
    SdStoreFilterGroupComponent,
    SdTitleComponent,
    SdProductCardComponent,
    FormsModule,
    SdFormInputComponent,
    SdButtonComponent,
    NgTemplateOutlet,
    SdCardStoreComponent,
    SdMapLegendComponent,
    SdTabGroupComponent,
    SdTabComponent,
    SdLoaderComponent,
    TranslateModule
  ],
  providers: [
    { provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, useValue: { overlayPanelClass: '' } }
  ]
})
export class SdStoreLocatorComponent extends SdThemeComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
  @Input()
  isUserPickupInfoFormValid: boolean;

  @Input()
  isLoading = false;

  @Input()
  isCheckout = false;

  @Input()
  filters: IStoreFilter[];

  @Input()
  searchCountryRestrictions: string | string[];

  @Input()
  title: string;

  @Input()
  mapSelectAction = 'pickup.actions.choose';

  @Input()
  isPickup = false;

  @Input()
  hideCardActions = false;

  @Input()
  repairCenter = false;

  @Input()
  product: IProductCard;

  @Input()
  isInsideModal = false;

  @Input()
  isEmbedded = false;

  @Input()
  selectedPickupId: string;

  @ViewChild('searchInput')
  public searchElementRef: SdFormInputComponent;

  @ViewChild('map')
  public map: SdGoogleMapComponent;

  @ViewChild('storesDiv', { read: ElementRef })
  public storesDiv: ElementRef;

  @ViewChildren('storeCard')
  storeCard: QueryList<SdCardStoreComponent>;

  /**
   * Properties of `<sd-map-legend>` component
   */
  @Input()
  mapLegend: IMapLegend[] = [
    {
      iconName: 'icon-pin-filled',
      description: 'maplegend.description1',
      isBrand: true,
      type: StoreTypeEnum.store
    },
    {
      iconName: 'icon-pin-filled',
      description: 'maplegend.description2',
      isBrand: false,
      type: StoreTypeEnum.retailer
    },
    {
      iconName: 'icon-ups',
      description: '',
      isBrand: true,
      type: StoreTypeEnum.ups
    },
    {
      iconName: 'icon-colissimo',
      description: '',
      isBrand: false,
      type: StoreTypeEnum.colissimo,
      color: '#e61e25'
    },
    {
      iconName: 'icon-gls',
      description: '',
      isBrand: false,
      type: StoreTypeEnum.gls
    },
    {
      iconName: 'icon-econt',
      description: '',
      isBrand: false,
      type: StoreTypeEnum.econt
    },
    {
      iconName: 'icon-hermes',
      description: '',
      isBrand: false,
      type: StoreTypeEnum.hermes
    }
  ];

  /**
   * Properties of left panel "find stores"
   */
  @Input()
  locator: ILocator = {
    titleLevel: 3,
    titleLevelCard: 3,
    button: {
      icon: 'icon-target'
    }
  };

  /**
   * List of stores
   */
  @Input()
  stores: ICardStore[] = [];

  @Input()
  hasNoResults = false;

  /**
   * Map boundaries
   */
  @Input()
  bounds: google.maps.LatLngBoundsLiteral;

  /**
   * Country to center the store-locator map
   */
  @Input()
  centerOnCountry: { name: string; isocode: string };

  @Input()
  searchWord: string;

  @Output()
  locate = new EventEmitter<{ location: any; address?: any }>();

  @Output()
  selectedStore = new EventEmitter<ICardStore>();

  @Output()
  storeFiltered = new EventEmitter<IStoreFilter[]>();

  @Output()
  chooseSelected = new EventEmitter<ICardStore>();

  @Output()
  activeTabindex = new EventEmitter<number>();

  @Output()
  productClicked = new EventEmitter<string>();

  @Output()
  productSellerModal = new EventEmitter<boolean>();

  @Output()
  itenaryClicked = new EventEmitter<string>();

  @Output()
  learnMoreClicked = new EventEmitter<ICardStore>();

  @Input()
  class = '';

  @Input()
  isHob = false;

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.isPickup ? 'is-pickup' : '',
      this.isEmbedded ? 'is-embedded' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  storeLocSliderConfig: SwiperOptions;
  currentSelectedCard: SdCardStoreComponent;
  userLocation;
  destination;
  itineraryUrl: string;
  autocomplete;
  filteredStores = [];
  markers = [];
  _filteredMarkers = [];
  isMobile$: boolean;
  mobileSubscription: Subscription;
  zoomClicked = 14;
  themesEnum = ThemesEnum;
  tabsMap: boolean;
  mapPinSvgPath = [];
  googleMap: IGoogleMap;
  currentSelectedStore: ICardStore;
  sizesEnum = SizesEnum;

  svgInPost =
    '<svg id="Warstwa_1" data-name="Warstwa 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20.61 26" width="39" height="40"><defs><style>.cls-1{fill:#404041;}.cls-2{fill:#ffcc04;}</style></defs><path class="cls-1" d="M10.74,0H9.88C8.23,0,0,.6,0,9.29,0,15.65,6.18,22,9.14,25l.76.78a.52.52,0,0,0,.41.18.55.55,0,0,0,.41-.18l.75-.78c3-3.05,9.14-9.39,9.14-15.75C20.61.6,12.39,0,10.74,0Z"/><path class="cls-2" d="M7.86,11a.09.09,0,0,0,.06-.08.1.1,0,0,0-.06-.09,6.2,6.2,0,0,0-2-.35,6.13,6.13,0,0,0-2,.35.09.09,0,0,0-.06.09.08.08,0,0,0,.06.08,5.84,5.84,0,0,0,2,.35A5.9,5.9,0,0,0,7.86,11Z"/><path class="cls-2" d="M10.49,6l0,0,.05,0a.08.08,0,0,0,0-.1,5.88,5.88,0,0,0-.82-1.82A5.82,5.82,0,0,0,8.39,2.63a.09.09,0,0,0-.1,0,.1.1,0,0,0,0,.1,5.94,5.94,0,0,0,.83,1.82A5.85,5.85,0,0,0,10.49,6Z"/><path class="cls-2" d="M6.61,7.54a6.38,6.38,0,0,0,1.93.62h0A.1.1,0,0,0,8.62,8,6.06,6.06,0,0,0,7,6.77a5.9,5.9,0,0,0-1.93-.61.09.09,0,0,0-.09,0,.09.09,0,0,0,0,.11A5.72,5.72,0,0,0,6.61,7.54Z"/><path class="cls-2" d="M14.72,15.87a5.1,5.1,0,0,1-4.14-3.24,3.15,3.15,0,0,0,1.15-.45.09.09,0,0,0,0-.09A.1.1,0,0,0,11.7,12c-.06,0-1.48-.47-1.39-1.77a5.05,5.05,0,0,1,4.41-4.31.09.09,0,0,0,0-.18A5.09,5.09,0,0,0,14,5.69a5.38,5.38,0,0,0-3.74,1.54,5.11,5.11,0,0,0-1.54,3.69A5.25,5.25,0,0,0,14,16.1h0a4.43,4.43,0,0,0,.72-.05.09.09,0,0,0,0-.18Z"/><path class="cls-2" d="M10.49,15.81a5.75,5.75,0,0,0-2.24,3.25.1.1,0,0,0,0,.1l.05,0,.05,0a5.61,5.61,0,0,0,1.42-1.43,5.77,5.77,0,0,0,.82-1.82.08.08,0,0,0,0-.1A.09.09,0,0,0,10.49,15.81Z"/><path class="cls-2" d="M8.62,13.79a.08.08,0,0,0,0-.1.07.07,0,0,0-.09,0,5.9,5.9,0,0,0-1.93.61A5.65,5.65,0,0,0,5,15.48a.08.08,0,0,0,0,.1s0,.05.08.05h0A5.9,5.9,0,0,0,7,15,5.83,5.83,0,0,0,8.62,13.79Z"/></svg>';
  svgPacketa =
    '<svg width="52" height="57" viewBox="0 0 52 57" fill="none" xmlns="http://www.w3.org/2000/svg"><g filter="url(#filter0_d_884_45573)"><path d="M8.75 26.1818C8.75 16.5474 16.4802 8.75 26 8.75C35.5198 8.75 43.25 16.5474 43.25 26.1818C43.25 30.1999 41.6547 33.4497 39.4741 36.2085C37.6178 38.5571 35.3722 40.5108 33.3436 42.2757C32.9791 42.5929 32.6216 42.9039 32.2746 43.2101L26 47.9679L19.7254 43.2101C19.3784 42.9039 19.0209 42.5929 18.6563 42.2757C16.6278 40.5108 14.3822 38.5571 12.5259 36.2085C10.3453 33.4497 8.75 30.1999 8.75 26.1818Z" fill="#BA1B02" stroke="white" stroke-width="1.5"/><path fill-rule="evenodd" clip-rule="evenodd" d="M26.5467 24.2234L26.12 24.4175L25.6933 24.2234L19.8359 21.3107L22.6288 20.2428L28.6413 23.1748L26.5467 24.2234ZM32.4428 21.3107L30.3481 22.3399L24.258 19.6214L26.1588 18.8835L32.4428 21.3107Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M26.3522 17.0194L34.0715 20.0097L32.9854 20.8835L26.1389 18.2039L19.273 20.8641L18.1869 19.9903L25.9061 17H26.3522V17.0194ZM33.1599 21.1165L34.44 20.6311L33.4897 28.5534L33.2957 28.9029L26.5268 35L26.3134 33.6019L32.3453 28.1845L33.1599 21.1165ZM18.9627 28.9029L18.7687 28.5534L17.8184 20.6311L19.0984 21.1165L19.9324 28.165L25.9643 33.5825L25.751 34.9806L18.9627 28.9029Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M30.5038 22.5923L32.4434 21.3884L31.6869 27.7768L26.1787 32.7282L26.4696 25.0583L28.894 23.5632L28.7389 27.3302L30.2323 26.2816L30.5038 22.5923Z" fill="#AAAAAA"/><path fill-rule="evenodd" clip-rule="evenodd" d="M25.7911 25.0583L26.082 32.7282L20.5932 27.7768L19.8174 21.3884L25.7911 25.0583Z" fill="#D7D7D7"/></g><defs><filter id="filter0_d_884_45573" x="0" y="0" width="52" height="56.9092" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset/><feGaussianBlur stdDeviation="4"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/> <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_884_45573"/><feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_884_45573" result="shape"/></filter></defs></svg>';
  svgEcont =
    '<svg  id="Calque_Econt"  width="39" height="40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 145.64 227.6"><defs><style>.cls-1{fill:#164282;}.cls-1,.cls-2{fill-rule:evenodd;stroke-width:0px;}.cls-2{fill:#fff;}</style></defs><path class="cls-1" d="m72.82,0c39.63,0,71.87,31.67,72.8,71.07h0,0c.01.58.02,1.17.02,1.75,0,39.46-53.52,116.59-70.78,154.78C56.58,189.8,0,112.42,0,72.82S32.61,0,72.82,0"/><path class="cls-2" d="m97.39,34.83h-40.18c-6.47,0-12.4,5.25-13.26,11.72l-7.79,58.62c-.85,6.46,3.72,11.72,10.18,11.72h40.18c6.01,0,11.54-4.88,12.32-10.88.79-6.02-3.45-10.88-9.45-10.88h-28.47s5.14-38.52,5.14-38.52h28.47c6.01,0,11.52-4.88,12.31-10.88.8-6.01-3.43-10.88-9.46-10.88Zm-14.48,28.04c-7.17,0-12.98,5.81-12.98,12.99s5.82,12.97,12.98,12.97,12.98-5.8,12.98-12.97-5.81-12.99-12.98-12.99h0Z"/></svg>';
  svgGls =
    '<svg id="Calque_Gls" width="39" height="40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 85.26 112.46"><defs><style>.cls-1{fill:#f7d11b;}.cls-1,.cls-2,.cls-3{stroke-width:0px;}.cls-2{fill:#fff;}.cls-3{fill:#041ab6;}</style></defs><path class="cls-3" d="m85.26,42.63c0,23.54-17.22,53.46-42.51,69.83C20.62,98.87,0,66.17,0,42.63S19.09,0,42.63,0s42.63,19.09,42.63,42.63Z"/><circle class="cls-1" cx="71.44" cy="44.03" r="3.4"/><path class="cls-2" d="m66.39,39.82c-1.06-7.05-8.62-4.39-9.12-6.66-.54-2.45,8.16,0,8.16,0v-5.44c-6.89-1.81-14.32-1.27-14.32,5.8,0,7.7,7.25,5.26,8.7,7.52,1.64,2.57-8.7.18-8.7.18v5.62s11.94,3.26,14.88-3.43c.49-1.12.59-2.38.4-3.59Z"/><polygon class="cls-2" points="41.85 26.84 35.51 26.84 35.51 47.43 48.92 47.43 48.92 41.95 41.85 41.95 41.85 26.84"/><path class="cls-2" d="m23.91,41.13h3.17c-1.45,1.18-6.62,2.72-6.16-4.62s10.6-3.08,10.6-3.08v-5.8c-17.67-5.44-21.84,13.14-14.05,18.13,7.8,4.99,12.24-1.27,12.24-1.27v2.95h3.81v-10.83h-9.61v4.53Z"/></svg>';
  svgHermes =
    '<svg id="Calque_Hermes" width="39" height="40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 180.63 194.52"><defs><style>.cls-1{fill:#0099d2;}.cls-1,.cls-2{stroke-width:0px;}.cls-2{fill:#fff;}</style></defs><polygon class="cls-1" points="0 0 0 174.53 70.47 174.53 90.38 194.52 108.68 174.53 180.63 174.53 180.63 0 0 0"/><path class="cls-2" d="m143.61,52.77c0-2.63-2.13-4.76-4.76-4.76H41.53c-2.63,0-4.76,2.13-4.76,4.76v23.18h106.83v-23.18Z"/><path class="cls-2" d="m36.77,103.53c0,15.88,12.87,28.75,28.75,28.75h73.32c2.63,0,4.76-2.13,4.76-4.76v-23.99H36.77Z"/><path class="cls-2" d="m140.73,36.07H38.69c-7.49,0-13.58,6.09-13.58,13.58v54.4c0,22.23,18.08,40.31,40.31,40.31h73.52c9.15,0,16.59-7.44,16.59-16.59V50.87c0-8.16-6.64-14.79-14.79-14.79Zm10.79,91.71c0,6.94-5.65,12.59-12.59,12.59h-73.52c-20.02,0-36.31-16.29-36.31-36.31v-54.4c0-5.28,4.3-9.58,9.58-9.58h102.05c5.95,0,10.79,4.84,10.79,10.79v76.91Z"/><polygon class="cls-2" points="63.49 81.9 61.15 81.9 60.14 86.35 62.32 86.35 63.49 81.9"/><polygon class="cls-2" points="62.01 87.85 59.66 87.85 58.66 92.3 60.84 92.3 62.01 87.85"/><polygon class="cls-2" points="60.53 93.59 58.18 93.59 57.18 98.04 59.35 98.04 60.53 93.59"/><polygon class="cls-2" points="53.45 98.04 55.94 98.04 56.94 93.59 49.67 93.59 49.67 94.53 53.45 98.04"/><polygon class="cls-2" points="58.42 87.85 43.7 87.85 43.7 88.88 47.23 92.3 57.42 92.3 58.42 87.85"/><polygon class="cls-2" points="59.81 81.9 37.72 81.9 37.72 83.24 41.07 86.35 58.85 86.35 59.81 81.9"/><path class="cls-2" d="m79.57,82.94s.32-1.04-.89-1.04h-3.26l-.09.46s.58.03.43.92c-.14.89-1.07,4.56-1.07,4.56h-4.82l1.18-4.91s.35-1.04-.75-1.04h-3.23l-.12.49s.58.09.35,1.07c-.23.98-3.03,11.74-3.43,13.41,0,0-.49,1.16.63,1.16h3.4l.14-.44s-.75.06-.26-1.79c.49-1.85,1.33-5.31,1.33-5.31h4.79l-1.64,6.46s-.35,1.07.72,1.07h3.43v-.41s-.75.03-.14-1.81c.61-1.85,3.29-12.87,3.29-12.87Z"/><path class="cls-2" d="m89.35,92.16c.78,0,1.52-.99,1.52-3.8s-1.54-3.34-4.3-3.34-5.76.76-6.95,6.7.53,6.31,4.85,6.31,3.87-.54,4.71-3.85h-.66s-.04,1.21-3.46,1.21-2.61-1.47-2.23-3.24h6.53Zm-2.68-4.83c2.15,0,1.11,2.75,1.11,2.75h-4.38c.43-1.88,1.11-2.75,3.26-2.75Z"/><path class="cls-2" d="m130.28,92.16c.78,0,1.52-.99,1.52-3.8s-1.54-3.34-4.3-3.34-5.76.76-6.95,6.7.53,6.31,4.85,6.31,3.87-.54,4.71-3.85h-.66s-.04,1.21-3.46,1.21-2.61-1.47-2.23-3.24h6.53Zm-2.68-4.83c2.15,0,1.11,2.75,1.11,2.75h-4.38c.43-1.88,1.11-2.75,3.26-2.75Z"/><path class="cls-2" d="m98.99,87.81c2.52,0,2.18-.29,2.61-2.51h-2.77c-.15,0-.3.06-.4.17l-1.37,1.37s.68-1.68-.44-1.68h-3.14l-.08.53s.72-.13.34,1.18-2.35,9.01-2.66,10.09c-.3,1.08.08,1.1,1.23,1.08s2.41,0,2.41,0l.15-.44s-.66-.11-.46-1.06,2.16-7.57,2.16-7.57c0,0-.13-1.16,2.39-1.16Z"/><path class="cls-2" d="m118.82,89.4c.77-3.01.88-4.37-2.59-4.37-2.57,0-2.94.75-3.62,1.94.11-1.01-.53-1.94-2.81-1.94s-2.79.53-3.49,1.74c.13-.72.35-1.61-.44-1.61h-3.16l-.13.47c.97.32.37,1.05.37,1.05,0,0-2.24,8.7-2.59,10.17-.35,1.47.37,1.2.37,1.2h3.23v-.5c-.53-.15-.15-1.14-.15-1.14,0,0,1.34-5.71,1.8-6.76.46-1.05.35-1.98,2.17-1.98s1.08,1.67,1.08,1.67c0,0-1.52,6.02-1.89,7.29s.15,1.41.15,1.41h3.49l.02-.49c-.48-.22-.26-1.1-.26-1.1,0,0,1.41-5.53,1.8-6.59.4-1.05.4-2.24,2.17-2.24s1.23,1.32,1.23,1.32c0,0-1.6,6.41-1.93,7.75-.33,1.34.2,1.35.2,1.35h3.43l-.04-.56c-.57-.24-.2-1.12-.2-1.12,0,0,1.03-3.95,1.8-6.96Z"/><path class="cls-2" d="m138,85.17c-2.43-.11-3.54,1.85-3.99,4.25-.45,2.41,1.05,2.6,2.38,3.01,1.33.41,3.01.15,3.01,1.52s-1.48,1.37-1.48,1.37c0,0-.24-.02-2.3-.02s-2-.9-2-.9h-.71s-.39,1.59-.69,2.62c-.3,1.03.58,1.01.58,1.01h4.75c3.76,0,4.51-1.33,5.11-4.1.6-2.77-1.01-3.44-2.23-3.71-1.22-.27-2.88-.5-2.88-1.52s1.22-1,1.22-1h3.37c1.48,0,1.52-2.53,1.52-2.53,0,0-3.24.11-5.67,0Z"/></svg>';

  centerMap: google.maps.LatLngLiteral | google.maps.LatLng;

  constructor(
    private cdRef: ChangeDetectorRef,
    private deviceService: DeviceService,
    private sdLibConfig: SdLibConfig,
    private ngZone: NgZone
  ) {
    super();

    this.googleMap = {
      ...this.sdLibConfig.get().googleMap,
      markers: this.markers
    };
  }

  ngOnInit() {
    if (this.centerOnCountry) {
      this.centerMapOnCountry();
    } else {
      this.centerMap = this.googleMap.center;
    }

    if (!this.isPickup) {
      this.searchByUserPosition();
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.cdRef.markForCheck();
      this.autocomplete = new google.maps.places.Autocomplete(
        this.searchElementRef?.input.nativeElement
      );

      this.autocomplete?.setFields(['geometry', 'address_components']);

      if (this.searchCountryRestrictions) {
        this.autocomplete.setComponentRestrictions({
          country: this.searchCountryRestrictions
        });
      }

      this.autocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => {
          const place = this.autocomplete.getPlace();
          if (place.geometry !== undefined && place.geometry !== null) {
            this.userLocation = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };
            this.searchByInput(this.userLocation, place.address_components);
          }
        });
      });

      /* Get Pin SVG Path by types */
      setTimeout(() => {
        this.mapLegend.forEach((legend: IMapLegend) => {
          if (!this.mapPinSvgPath[legend.type]) {
            if (legend.iconName === 'icon-ups') {
              this.mapPinSvgPath[legend.type?.toLowerCase()] =
                'data:image/svg+xml;utf-8, \
              <svg  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 39.4 48.3" width="39" height="40"> \
                <path fill="rgb(52, 27, 20)" d="M19.7 46.5c.4-.2 10.8-4.7 14.1-7.4 3.4-2.8 5.2-6.8 5.2-11.6V5l-.3-.1C30.4.4 20.1.7 19.6.7 19.2.7 8.9.4.6 4.9L.4 5v22.6c0 4.8 1.8 8.8 5.2 11.6 3.3 2.7 13.7 7.2 14.1 7.3"/> \
                <path  fill="rgb(255, 180, 6)" d="M19.7 47s-11-4.8-14.4-7.5C1.7 36.5 0 32.4 0 27.7V4.3C8.7-.4 19.7 0 19.7 0s11-.4 19.7 4.3v23.3c0 4.7-1.7 8.8-5.3 11.8-3.4 2.8-14.4 7.6-14.4 7.6M1.6 27.7c0 4.4 1.6 8 4.7 10.5 2.8 2.3 11.1 6 13.4 7 2.3-1 10.7-4.8 13.4-7 3.1-2.5 4.7-6.2 4.7-10.5v-23c-11.6-1.1-25.4-.5-36.2 9.5v13.5z"/> \
                <path  fill="rgb(255, 180, 6)" d="M30.8 24.4c1.5.9 2.1 1.5 2.2 2.6 0 1.2-.8 1.9-2.1 1.9-1.1 0-2.4-.6-3.3-1.4v3.2c1.1.6 2.4 1.1 3.8 1.1 3.4 0 5-2.4 5-4.6.1-2-.5-3.6-3.4-5.3-1.3-.8-2.3-1.3-2.3-2.5s1.1-1.7 2-1.7c1.2 0 2.4.7 3.1 1.4v-3c-.6-.5-1.9-1.2-3.8-1.1-2.3.1-4.7 1.7-4.7 4.5.1 1.9.7 3.3 3.5 4.9M19 31.6c.3.1.8.2 1.6.2 3.9 0 6.1-3.5 6.1-8.5 0-5.1-2.3-8.2-6.4-8.2-1.9 0-3.4.4-4.7 1.2v22.6H19v-7.3zM19 18c.3-.1.8-.3 1.2-.3 2 0 2.8 1.6 2.8 5.5 0 3.8-1 5.6-3 5.6-.5 0-.9-.1-1.1-.2V18zM8.5 31.8c2.1 0 3.9-.5 5.2-1.4V15.3h-3.5v13.1c-.4.3-.9.4-1.6.4-1.6 0-1.8-1.5-1.8-2.4V15.3H3.3v10.9c0 3.7 1.8 5.6 5.2 5.6M31.8 45.3v2.3h.4v-.9h.1l.6.9h.5s-.6-.9-.7-1c.3-.1.5-.3.5-.6s-.2-.7-.8-.7h-.6zm.6.3c.3 0 .4.2.4.3 0 .2-.1.4-.5.4h-.1v-.7h.2z"/> \
                <path fill="rgb(255, 180, 6)" d="M34 46.4c0 .8-.7 1.5-1.5 1.5s-1.5-.7-1.5-1.5.7-1.5 1.5-1.5c.9 0 1.5.7 1.5 1.5m-1.5-1.9c-1 0-1.9.8-1.9 1.9 0 1 .8 1.9 1.9 1.9 1 0 1.9-.8 1.9-1.9 0-1-.9-1.9-1.9-1.9"/> \
              </svg>';
            } else if (legend.iconName === 'icon-inPost') {
              this.mapPinSvgPath[legend.type?.toLowerCase()] =
                'data:image/svg+xml;utf-8, ' +
                encodeURIComponent(this.svgInPost);
            } else if (legend.iconName === 'icon-packeta') {
              this.mapPinSvgPath[legend.type?.toLowerCase()] =
                'data:image/svg+xml;utf-8, ' +
                encodeURIComponent(this.svgPacketa);
            } else if (legend.iconName === 'icon-gls') {
              this.mapPinSvgPath[legend.type?.toLowerCase()] =
                'data:image/svg+xml;utf-8, ' + encodeURIComponent(this.svgGls);
            } else if (legend.iconName === 'icon-econt') {
              this.mapPinSvgPath[legend.type?.toLowerCase()] =
                'data:image/svg+xml;utf-8, ' +
                encodeURIComponent(this.svgEcont);
            } else if (legend.iconName === 'icon-hermes') {
              this.mapPinSvgPath[legend.type?.toLowerCase()] =
                'data:image/svg+xml;utf-8, ' +
                encodeURIComponent(this.svgHermes);
            } else {
              this.mapPinSvgPath[legend.type?.toLowerCase()] =
                this.document.body
                  .querySelector('#' + legend.iconName + ' path')
                  ?.getAttribute('d');
            }
          }
        });
      });

      /* Set Filters Color depending of mapLegend */
      if (this.filters) {
        this.filters.forEach((filter: IStoreFilter) => {
          filter['color'] = this.getColorByStoreType(filter.type);
        });
      }

      /**
       * @TODO: space between might be problematic if brand decided to use
       * a different brand--ratio, see if it's possible to map it on a css value
       */
      this.storeLocSliderConfig = {
        navigation: {
          prevEl: '.c__tabs-nav-prev',
          nextEl: '.c__tabs-nav-next',
          disabledClass: 'is-disabled'
        },
        breakpoints: {
          0: {
            slidesPerView: 1.3,
            spaceBetween: 12
          },
          768: {
            slidesPerView: 2.3,
            spaceBetween: 12
          },
          1280: {
            slidesPerView: 3.3,
            spaceBetween: 12
          }
        },
        observer: true,
        observeParents: true
      };
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (!changes.isUserPickupInfoFormValid) {
      this.googleMap = {
        ...this.sdLibConfig.get(this.brand).googleMap,
        markers: this.markers
      };
    }
    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });

    if (changes?.stores) {
      this.isLoading = false;
      this.stores.forEach((store: ICardStore) => {
        store['color'] = this.getColorByStoreType(store.type);
      });
      this.filteredStores = Object.assign([], this.stores.filter(
        (store)=> !!store.position.latitude && !!store.position.longitude
      ));
      const iconOptions = [
        StoreTypeEnum.ups.toString(),
        StoreTypeEnum.inpost.toString(),
        StoreTypeEnum.packeta.toString(),
        StoreTypeEnum.gls.toString(),
        StoreTypeEnum.econt.toString(),
        StoreTypeEnum.hermes.toString()
      ];
      this.markers = this.filteredStores.map((store) => ({
        position: {
          lat: store.position.latitude,
          lng: store.position.longitude
        },
        color: this.getColorByStoreType(store.type),
        type: store.type,
        name: store.name,
        id: store.id,
        iconSvgPath: this.mapPinSvgPath[store.type?.toLowerCase()],
        ...((iconOptions.includes(store.type?.toLowerCase()) && {
          url: this.mapPinSvgPath[store.type?.toLowerCase()]
        }) ||
          {})
      }));
      this.googleMap.markers = this.markers;
      this._filteredMarkers = Object.assign([], this.markers);

      if (!this.bounds && this.stores?.length) {
        const generatedBounds = new google.maps.LatLngBounds();
        this.markers.forEach((marker) => {
          generatedBounds.extend(marker.position);
        });
        this.map?.setBounds(generatedBounds);
      }
    }

    if (changes?.bounds && this.bounds) {
      this.map?.setBounds(this.bounds);
    }

    if (
      changes?.centerOnCountry?.currentValue?.name !==
      changes?.centerOnCountry?.previousValue?.name
    ) {
      this.centerMapOnCountry();
    }

    if (
      changes?.searchCountryRestrictions &&
      this.searchCountryRestrictions &&
      this.autocomplete
    ) {
      this.autocomplete.setComponentRestrictions({
        country: this.searchCountryRestrictions
      });
    }

    this.cdRef.markForCheck();
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
  }

  centerMapOnCountry() {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      {
        address: this.centerOnCountry?.name,
        region: this.centerOnCountry?.isocode
      },
      (results: google.maps.GeocoderResult[]) => {
        this.centerMap = results
          ? results[0]?.geometry.location
          : this.googleMap.center;
      },
    );
  }

  geocodeLatLng(location) {
    const geocoder = new google.maps.Geocoder();
    const latLng = new google.maps.LatLng(location.lat, location.lng);
    const request = {
      location: latLng
    };

    geocoder.geocode(
      request,
      (
        results: google.maps.GeocoderResult[],
        status: google.maps.GeocoderStatus,
      ) => {
        if (status === google.maps.GeocoderStatus.OK) {
          const resultAddress = results[0].address_components;
          this.locate.emit({ location, address: resultAddress });
        }
      },
    );
  }

  /**
   * Dispatch event with location wanted to get stores
   */
  emitLocateEvent(location, typedValue?) {
    this.isLoading = true;

    if (!this.isPickup) {
      this.locate.emit({ location });
    } else {
      const postalCode = typedValue?.filter(
        (address) => address.types.indexOf('postal_code') > -1,
      ).length;
      if (!typedValue || !postalCode) {
        this.geocodeLatLng(location);
      } else {
        this.locate.emit({ location, address: typedValue });
      }
    }
  }

  /**
   * Get User position, center the map on the user position and getStores corresponding of the user position
   */
  public searchByUserPosition() {
    navigator.geolocation.getCurrentPosition((position) => {
      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      this.userLocation = coords;
      this.emitLocateEvent(coords);
      this.map.centerMap(coords, this.map.zoom);
    });
  }

  /**
   * Handle zipcode input field
   */
  public searchByInput(location, typedValue) {
    this.emitLocateEvent(location, typedValue);
  }

  updateStoreStatus(event: SdCardStoreComponent) {
    if (event.isCardOpened === true || event.selected === true) {
      this.storeCard.toArray().forEach((el) => {
        if (el.storeInfo.id !== event.storeInfo.id) {
          el.isCardOpened = false;
          el.selected = false;
          el.readOnly = true;
        } else {
          el.readOnly = false;
        }
      });
      this.generateItinerary(event.storeInfo.position, this.userLocation);
      this.currentSelectedStore = event.storeInfo;
    } else {
      this.storeCard.toArray().forEach((el) => {
        el.isCardOpened = false;
        el.selected = false;
        el.readOnly = false;
      });
      this.currentSelectedStore = null;
      this.resetItinerary(false);
    }

    this.cdRef.markForCheck();
  }

  generateItinerary(storePosition: IPosition, userLocation) {
    this.destination = storePosition.latitude + ',' + storePosition.longitude;
    const baseRequest = 'https://www.google.com/maps/dir/?api=1&origin=';
    const origin = userLocation
      ? userLocation.lat + ',' + userLocation.lng
      : userLocation;
    const destination = '&destination=' + this.destination;
    const travelMode = '&travelmode=driving';
    this.itineraryUrl = baseRequest + origin + destination + travelMode;
  }

  resetItinerary(event: boolean): void {
    if (event === false) {
      this.itineraryUrl = undefined;
    }
  }

  markerClick(markerId: string) {
    this.currentSelectedCard = this.storeCard
      .toArray()
      .find((store) => store.storeInfo.id === markerId);
    if (this.currentSelectedCard) {
      this.currentSelectedCard.triggerClick();
      if (!this.isMobile$ && !this.isEmbedded) {
        setTimeout(() => {
          const storeScrollHeight =
            this.currentSelectedCard?.elRef?.nativeElement?.parentElement
              ?.offsetTop;
          this.storesDiv.nativeElement.scrollTo({
            top: storeScrollHeight,
            behavior: 'smooth'
          });
        }, 300);
      }
    }
  }

  storeClick(cardStore: SdCardStoreComponent, close?: boolean) {
    const selectedMarker = this.markers.find(
      (marker) => marker.id === cardStore.storeInfo.id,
    );
    if (selectedMarker) {
      if ((cardStore.isCardOpened || cardStore.selected) && !close) {
        this.currentSelectedCard = this.storeCard
          .toArray()
          .find((store) => store.storeInfo.id === selectedMarker.id);
        const selectedMapMarker = this.map.markers.find(
          (marker) =>
            marker.position.lat() ===
              cardStore.storeInfo.position['latitude'] &&
            marker.position.lng() === cardStore.storeInfo.position['longitude'],
        );
        this.map.setSelectMarker(selectedMapMarker);
      } else {
        this.currentSelectedCard = null;
        this.map.resetAllMarkers();
        if (!this.bounds) {
          const generatedBounds = new google.maps.LatLngBounds();
          this.markers.forEach((marker) => {
            generatedBounds.extend(marker.position);
          });
          this.map?.setBounds(generatedBounds);
        } else {
          this.map?.setBounds(this.bounds);
        }
        this.googleMap.zoom = this.map.zoom;
      }
      this.map.centerMap(selectedMarker.position, this.map.zoom);
    }

    this.updateStoreStatus(cardStore);
  }

  repairCenterClicked(storeInfo: ICardStore) {
    this.currentSelectedStore = storeInfo;
    this.chooseSelected.emit(this.currentSelectedStore);
  }

  toggleButtons(event) {
    this.tabsMap = event === 1;
  }

  scrollToActive(event: number) {
    this.toggleButtons(event);

    if (event === 0) {
      this.storeCard.toArray().findIndex((store: SdCardStoreComponent) => {
        return store.selected === true || store.isCardOpened === true;
      });
    }
    this.activeTabindex.emit(event);
  }

  confirmChoice(storeInfo: ICardStore) {
    this.selectedStore.emit(storeInfo);
  }

  onStoreFiltered(event) {
    this.storeFiltered.emit(event);
  }

  getColorByStoreType(type: string) {
    const storeType = type?.toLowerCase();
    const legendWithColor = this.mapLegend?.filter((el) => {
      const legendType = el.type?.toLowerCase();
      return legendType && (legendType === storeType);
    });
    return legendWithColor[0] ? legendWithColor[0].color : '';
  }

  onProductClick(event: string) {
    this.productClicked.emit(event);
  }

  toggleProductSellerModal(event) {
    this.productSellerModal.emit(event);
  }

  itenaryClick() {
    this.itenaryClicked.emit(this.itineraryUrl);
  }

  learnMoreClick() {
    this.learnMoreClicked.emit(this.currentSelectedStore);
  }
}
